import { motion } from "framer-motion";
import { Star, BookOpen, Leaf, CalendarCheck, Zap, ShieldCheck } from "lucide-react";
import EbookMockup from "@/components/EbookMockup";
import { scrollToId } from "@/lib/site";

const BADGES = [
  { icon: BookOpen, top: "170+ Pages", sub: "Practical Guide" },
  { icon: Leaf, top: "100% Natural", sub: "Herbal Approach" },
  { icon: CalendarCheck, top: "30-Day Routine", sub: "Step by Step" },
  { icon: Zap, top: "Instant Access", sub: "Yours Forever" },
];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  return (
    <section id="overview" className="relative pt-[160px] sm:pt-[180px] pb-16 sm:pb-24 overflow-hidden" data-testid="hero-section">
      <div className="absolute -top-32 -right-40 w-[560px] h-[560px] rounded-full bg-[#1C3A27]/[0.06] blur-3xl pointer-events-none" />
      <div className="absolute top-64 -left-48 w-[480px] h-[480px] rounded-full bg-[#D4A359]/10 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* badge strip */}
        <motion.div
          initial={{ opacity: 0, y: -14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="flex flex-wrap justify-center gap-x-8 gap-y-3 mb-12 sm:mb-16"
          data-testid="hero-badge-strip"
        >
          {BADGES.map(({ icon: Icon, top, sub }) => (
            <div key={top} className="flex items-center gap-2.5">
              <Icon className="w-5 h-5 text-[#1C3A27]" strokeWidth={1.8} />
              <div className="leading-tight">
                <p className="text-xs font-extrabold tracking-wide text-[#1A241E] uppercase">{top}</p>
                <p className="text-[11px] text-[#5C6A60]">{sub}</p>
              </div>
            </div>
          ))}
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-14 lg:gap-8 items-center">
          <div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25 }}
              className="text-xs sm:text-sm font-bold tracking-[0.24em] uppercase text-[#D4A359] mb-5"
              data-testid="hero-eyebrow"
            >
              A practical guide for real, lasting results
            </motion.p>

            <h1 className="font-display font-bold tracking-tight text-[#1A241E] text-4xl sm:text-5xl lg:text-[64px] leading-[1.04]" data-testid="hero-headline">
              <Line delay={0.35}>Lose Weight</Line>
              <Line delay={0.47}><span className="italic text-[#1C3A27]">Naturally</span> —</Line>
              <Line delay={0.59}>One Cup at a Time.</Line>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-6 text-base sm:text-lg text-[#5C6A60] leading-relaxed max-w-xl"
              data-testid="hero-subheadline"
            >
              The Sustainable Tea &amp; Weight Loss Method is a 170+ page, science-informed guide:
              tea rituals, satisfying meals, gentle habits and a 30-day routine that fits real life —
              no crash diets, no guilt, no perfectionism.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7 }}
              className="mt-6 inline-flex items-center gap-3 rounded-full bg-white border border-[#1C3A27]/10 px-4 py-2 shadow-sm"
              data-testid="hero-rating-pill"
            >
              <span className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#D4A359] text-[#D4A359]" />
                ))}
              </span>
              <span className="text-sm font-bold text-[#1A241E]">4.9/5</span>
              <span className="text-xs text-[#5C6A60]">from readers building healthier routines</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.12, duration: 0.7 }}
              className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => scrollToId("pricing")}
                className="cta-shine relative overflow-hidden rounded-full bg-[#1C3A27] text-[#FAF7F2] font-extrabold text-sm sm:text-base px-8 py-4 tracking-wide hover:bg-[#12261A] transition-colors duration-300 shadow-[0_18px_40px_-12px_rgba(18,38,26,0.5)]"
                data-testid="hero-cta-button"
              >
                GET MY DIGITAL GUIDE — $22
              </motion.button>
              <div className="flex items-center gap-2 text-xs text-[#5C6A60]" data-testid="hero-guarantee-tag">
                <ShieldCheck className="w-4 h-4 text-[#1C3A27]" />
                30-day money-back guarantee · Instant PDF download
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.55, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-[420px] h-[420px] rounded-full bg-[#1C3A27]/[0.07] blur-2xl" />
            </div>
            <EbookMockup />
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.3, duration: 0.8 }}
              className="hidden sm:block absolute -left-4 bottom-8 w-36 rounded-xl overflow-hidden shadow-xl ring-1 ring-[#1C3A27]/10 rotate-[-6deg]"
            >
              <img
                src="https://images.unsplash.com/photo-1678890565859-a2dadf52a48f?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Fresh lemon and ginger on a board"
                className="w-full h-24 object-cover"
              />
              <p className="bg-white text-[10px] font-bold text-[#1C3A27] px-3 py-2 tracking-wide uppercase">Real ingredients</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.45, duration: 0.8 }}
              className="hidden sm:block absolute -right-2 top-10 w-32 rounded-xl overflow-hidden shadow-xl ring-1 ring-[#1C3A27]/10 rotate-[5deg]"
            >
              <img
                src="https://images.pexels.com/photos/6694156/pexels-photo-6694156.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Dried herbs in wooden spoons"
                className="w-full h-20 object-cover"
              />
              <p className="bg-white text-[10px] font-bold text-[#1C3A27] px-3 py-2 tracking-wide uppercase">40+ recipes</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
